'use client';

import { ISalePageDetailV2Response } from '@/api/types';
import { URLS } from '@/urls';
import {
  secondaryCreatorDeliveryInfo,
  secondaryCreatorRefundInfo,
  secondaryCreatorReservationDeliveryInfo,
  secondaryCreatorReservationRefundInfo,
} from './const';
import { SalePagePurchaseGuideSections } from './PurchaseGuideSection';
import { getGuideSections } from './purchaseGuideUtils';

interface Props {
  salePage?: Partial<ISalePageDetailV2Response>;
}

const getSecondaryCreatorInfo = (
  salePage?: Partial<ISalePageDetailV2Response>
): { channelName: string; parentChannelName: string } | undefined => {
  const channel = salePage?.channel;
  if (!channel?.parentChannel?.name) return undefined;
  return {
    channelName: channel.name ?? '',
    parentChannelName: channel.parentChannel.name,
  };
};

const getSecondaryCreatorPhysicalSections = (
  salePage: Partial<ISalePageDetailV2Response>
) => {
  if (salePage.physical?.method === 'RESERVATION') {
    return {
      delivery: secondaryCreatorReservationDeliveryInfo,
      refund: secondaryCreatorReservationRefundInfo,
    };
  }
  return {
    delivery: secondaryCreatorDeliveryInfo,
    refund: secondaryCreatorRefundInfo,
  };
};

const SecondaryCreatorDigitalGuide = ({
  salePage,
}: {
  salePage: Partial<ISalePageDetailV2Response>;
}) => {
  const digitalSections = getGuideSections(salePage);

  if (!digitalSections) return null;

  return (
    <SalePagePurchaseGuideSections
      title="지급 / 환불 안내"
      sections={digitalSections}
      qna={{ label: '자주 묻는 질문', href: URLS.FAQ }}
    />
  );
};

const SecondaryCreatorPhysicalGuide = ({
  salePage,
}: {
  salePage: Partial<ISalePageDetailV2Response>;
}) => {
  const { delivery, refund } = getSecondaryCreatorPhysicalSections(salePage);

  return (
    <>
      <SalePagePurchaseGuideSections
        title="배송 정보"
        sections={delivery}
        qna={{ label: '배송 FAQ', href: URLS.FAQ }}
      />
      <SalePagePurchaseGuideSections
        title="교환 / 환불 정보"
        sections={refund}
        qna={{ label: '교환/환불 FAQ', href: URLS.FAQ }}
      />
    </>
  );
};

const SecondaryCreatorGuide = ({
  salePage,
}: {
  salePage: Partial<ISalePageDetailV2Response>;
}) => {
  if (salePage.type === 'DIGITAL') {
    return <SecondaryCreatorDigitalGuide salePage={salePage} />;
  }

  return <SecondaryCreatorPhysicalGuide salePage={salePage} />;
};

export const ComprehensivePurchaseGuide = ({ salePage }: Props) => {
  if (!salePage?.type) {
    return null;
  }

  const isSecondaryCreator = !!getSecondaryCreatorInfo(salePage);
  if (isSecondaryCreator) {
    return <SecondaryCreatorGuide salePage={salePage} />;
  }

  const sections = getGuideSections(salePage);
  if (!sections) {
    return null;
  }

  const isDigital = salePage.type === 'DIGITAL';
  const title = isDigital ? '지급 / 환불 안내' : '배송 / 교환 / 환불 안내';

  return (
    <SalePagePurchaseGuideSections
      title={title}
      sections={sections}
      qna={{ label: '자주 묻는 질문', href: URLS.FAQ }}
    />
  );
};
